import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Studio overlay for choosing which full-assessment questions belong in LITE.
 */
export default function LiteQuestionPicker({
  open = false,
  onClose,
  fullSteps = [],
  selectedIds = [],
  defaultIds = [],
  onChange,
  onSave,
  onReset,
  saving = false,
  dirty = false,
}) {
  const [activePillarIdx, setActivePillarIdx] = useState(0);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const defaultSet = useMemo(() => new Set(defaultIds), [defaultIds]);

  const activeStep = fullSteps[activePillarIdx] || fullSteps[0];
  const activeQuestions = activeStep?.questions || [];
  const activeSelectedCount = activeQuestions.filter((q) =>
    selectedSet.has(q.id)
  ).length;
  const activeAllSelected =
    activeQuestions.length > 0 && activeSelectedCount === activeQuestions.length;

  useEffect(() => {
    if (!open) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
      if (e.key === "ArrowLeft" && !saving) {
        setActivePillarIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === "ArrowRight" && !saving) {
        setActivePillarIdx((i) => Math.min(fullSteps.length - 1, i + 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, saving, onClose, fullSteps.length]);

  useEffect(() => {
    if (!open) return;
    setActivePillarIdx(0);
  }, [open]);

  useEffect(() => {
    if (!fullSteps.length) return;
    setActivePillarIdx((idx) => Math.min(idx, fullSteps.length - 1));
  }, [fullSteps.length]);

  const toggle = (qid) => {
    const next = new Set(selectedSet);
    if (next.has(qid)) next.delete(qid);
    else next.add(qid);
    onChange?.(Array.from(next));
  };

  const selectPillar = (step, selectAll) => {
    const qids = (step.questions || []).map((q) => q.id).filter(Boolean);
    const next = new Set(selectedSet);
    qids.forEach((id) => {
      if (selectAll) next.add(id);
      else next.delete(id);
    });
    onChange?.(Array.from(next));
  };

  if (!open) return null;

  const saveBtn = (
    <button
      type="button"
      onClick={onSave}
      disabled={saving || !dirty || selectedIds.length === 0}
      aria-label="Save LITE question selection"
      className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#004F73] text-white hover:opacity-90 disabled:opacity-50"
    >
      {saving ? "Saving…" : "Save LITE set"}
    </button>
  );

  const modal = (
    <div
      className="lite-picker-overlay fixed inset-0 z-[200] flex flex-col bg-slate-900/40 backdrop-blur-[2px] p-2 sm:p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lite-picker-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="lite-picker-shell flex flex-1 min-h-0 w-full max-w-[1440px] mx-auto flex-col overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200/90 bg-white shadow-2xl">
        {/* Compact top bar */}
        <div className="shrink-0 flex flex-wrap items-center gap-2 sm:gap-3 border-b border-slate-200 px-3 sm:px-4 py-2.5 bg-white">
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            <span aria-hidden>←</span>
            Back to survey
          </button>
          <span className="hidden sm:inline text-slate-300">|</span>
          <h2
            id="lite-picker-title"
            className="text-sm font-semibold text-slate-900"
          >
            Configure LITE questions
          </h2>
          <span className="text-xs font-medium text-[#004F73] bg-[#004F73]/8 border border-[#004F73]/15 rounded-full px-2.5 py-0.5 tabular-nums">
            {selectedIds.length} selected
          </span>
          {dirty ? (
            <span className="text-xs font-medium text-amber-700">Unsaved</span>
          ) : null}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onReset}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Reset default
          </button>
        </div>

        {/* Main workspace — sidebar + large question list */}
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[15rem_minmax(0,1fr)] overflow-hidden bg-slate-50/50">
          <aside className="min-h-0 overflow-y-auto border-b md:border-b-0 md:border-r border-slate-200 bg-white">
            <nav className="p-2 space-y-1">
              {fullSteps.map((step, idx) => {
                const questions = step.questions || [];
                const selectedInPillar = questions.filter((q) =>
                  selectedSet.has(q.id)
                ).length;
                const isActive = idx === activePillarIdx;
                return (
                  <button
                    key={step.step ?? step.title}
                    type="button"
                    onClick={() => setActivePillarIdx(idx)}
                    className={`w-full text-left rounded-lg px-2.5 py-2 transition ${
                      isActive
                        ? "bg-[#004F73]/10 text-[#004F73]"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <p className="text-xs font-semibold leading-snug">
                      {step.title || `Pillar ${step.step}`}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                      {selectedInPillar}/{questions.length}
                    </p>
                  </button>
                );
              })}
            </nav>
          </aside>

          <section className="min-h-0 flex flex-col overflow-hidden">
            {/* Slim pillar toolbar */}
            <div className="shrink-0 flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 sm:px-4 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {activeStep?.title || `Pillar ${activeStep?.step || 1}`}
                </p>
                <p className="text-[11px] text-slate-500">
                  {activeSelectedCount} of {activeQuestions.length} in this pillar
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => selectPillar(activeStep, !activeAllSelected)}
              >
                {activeAllSelected ? "Clear all" : "Select all"}
              </button>
            </div>

            {/* Question list — takes all remaining height */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <ul className="divide-y divide-slate-100">
                {activeQuestions.map((q) => {
                  const qid = q.id;
                  const checked = selectedSet.has(qid);
                  const isDefault = defaultSet.has(qid);
                  return (
                    <li key={qid}>
                      <label
                        className={`flex items-start gap-3 px-3 sm:px-4 py-3 cursor-pointer ${
                          checked ? "bg-[#004F73]/[0.05]" : "bg-white hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#004F73] focus:ring-[#004F73]/30"
                          checked={checked}
                          onChange={() => toggle(qid)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2 mb-0.5">
                            <span className="text-[11px] font-mono text-slate-400">
                              {qid}
                            </span>
                            {isDefault ? (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-[#004F73]/80">
                                default
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={`text-sm leading-relaxed ${
                              checked ? "text-slate-900" : "text-slate-700"
                            }`}
                          >
                            {q.text || "(untitled)"}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
                {activeQuestions.length === 0 ? (
                  <li className="px-4 py-8 text-sm text-slate-500 text-center">
                    No questions in this pillar.
                  </li>
                ) : null}
              </ul>
            </div>
          </section>
        </div>

        {/* Compact bottom bar */}
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 sm:px-4 py-2.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setActivePillarIdx((i) => Math.max(0, i - 1))}
              disabled={activePillarIdx <= 0}
            >
              ← Previous
            </button>
            <span className="text-xs text-slate-500 tabular-nums px-1">
              {activePillarIdx + 1} / {fullSteps.length}
            </span>
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={() =>
                setActivePillarIdx((i) => Math.min(fullSteps.length - 1, i + 1))
              }
              disabled={activePillarIdx >= fullSteps.length - 1}
            >
              Next →
            </button>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => !saving && onClose?.()}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            {saveBtn}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
